import { useCallback, useEffect } from 'react';

/**
 * Starts or ends the GameAnalytics session when Unity reports idle / analytics
 * boundaries.
 *
 * Addresses Unity through the `analyticsSession.start` and
 * `analyticsSession.end` slots. Pass bridged `addEventListener` /
 * `removeEventListener` from `useUnityBridge` so each game contract resolves
 * them to its own build names.
 *
 * @param {object} options
 * @param {object} [options.gameGA] - Initialized GameAnalytics instance.
 * @param {object} [options.ga] - Initialized Google Analytics instance.
 * @param {Function} options.addEventListener - Bridged Unity addEventListener.
 * @param {Function} options.removeEventListener - Bridged Unity removeEventListener.
 */
export const useGameAnalyticsSession = ({
    gameGA,
    ga,
    addEventListener,
    removeEventListener,
}) => {
    const handleGameEndSession = useCallback(() => {
        gameGA?.endGameSession();
        ga?.basicGaEvent('game', 'end_ga_analytics_session');
    }, [gameGA, ga]);

    const handleGameStartSession = useCallback(() => {
        gameGA?.startGameSession();
        ga?.basicGaEvent('game', 'start_ga_analytics_session');
    }, [gameGA, ga]);

    useEffect(() => {
        addEventListener('analyticsSession.end', handleGameEndSession);

        return () =>
            removeEventListener('analyticsSession.end', handleGameEndSession);
    }, [addEventListener, removeEventListener, handleGameEndSession]);

    useEffect(() => {
        addEventListener('analyticsSession.start', handleGameStartSession);

        return () =>
            removeEventListener(
                'analyticsSession.start',
                handleGameStartSession
            );
    }, [addEventListener, removeEventListener, handleGameStartSession]);
};
